import React from 'react';
import type { ElementType } from 'react';
import { Link } from '@remix-run/react';
import {
    Breadcrumb,
    useAppDisplayModeContext,
    useTheme,
    useStyles,
} from '@archipro-design/aria';

import * as S from './PageHeader.style';
import { superscriptize } from '~/utils';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import PageDetailHeaderCTA from './page-detail-header-cta/PageDetailHeaderCTA';

interface PageHeaderProps {
    data: {
        Breadcrumbs: ProductPageLoaderData['product']['Breadcrumbs'];
        ID: ProductPageLoaderData['product']['ID'];
        Title: ProductPageLoaderData['product']['Title'];
        Professional: Pick<
            ProductPageLoaderData['product']['Professional'],
            | 'ID'
            | 'Title'
            | 'Link'
            | 'Country'
            | 'PricingDisabled'
            | 'Website'
            | 'PickupLocations'
            | 'CompanyLogo'
            | 'LogoBackgroundColor'
        >;
        Website?: ProductPageLoaderData['product']['Website'];
        EcommerceReady?: boolean;
        PrimaryCTA: ProductPageLoaderData['primaryCTA'];
        branchOptions: ProductPageLoaderData['branchOptions'];
    };
    isSticky?: boolean;
}

/**
 * The header of the product detail page.
 */
const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
    function PageHeader(
        {
            data: {
                Breadcrumbs,
                ID,
                Title,
                Professional,
                Website,
                PrimaryCTA,
                branchOptions,
            },
            isSticky = false,
        },
        ref
    ) {
        const { mobile } = useAppDisplayModeContext();
        const theme = useTheme();
        const breadcrumbColor = theme.siteVariables.colors.grey[300];
        const iconAdjustedTitle = superscriptize(Title);
        const { css } = useStyles({ isSticky });

        return (
            <div ref={ref} className={css(S.ProductHeaderContainer)}>
                <div className={css(S.PageHeader)}>
                    <div className="mt-auto flex justify-between pt-1.5 md:hidden">
                        {!isSticky && (
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                }}
                                items={Breadcrumbs.map((breadcrumb) => ({
                                    label: breadcrumb.label,
                                    href: breadcrumb.href,
                                    as: breadcrumb.as as ElementType,
                                    variables: {
                                        breadcrumbItemTextColor:
                                            breadcrumbColor,
                                    },
                                }))}
                                data-testid="breadcrumb-menu"
                                variables={{
                                    breadcrumbFontSize:
                                        theme.siteVariables.fontSizes.caption04,
                                }}
                            />
                        )}
                    </div>
                    <div className="flex w-full justify-between">
                        <div className={css(S.TitleContainer)}>
                            <h1
                                className="mb-0 mt-0 text-5 leading-[1.1] text-black md:text-20 md:leading-[1.1] md:tracking-[-2.4px]"
                                dangerouslySetInnerHTML={{
                                    __html: iconAdjustedTitle,
                                }}
                            />

                            <Link
                                to={Professional.Link}
                                className="mb-0 mt-0 text-4 leading-[1.1] text-[#b6b6b6] hover:text-gray-400 md:text-16 md:leading-[1.1] md:tracking-[-2.4px]"
                            >
                                {Professional.Title}
                            </Link>
                        </div>
                        {!mobile && (
                            <div className="mb-8 flex items-end justify-end">
                                <PageDetailHeaderCTA
                                    website={Website || undefined}
                                    pageID={ID}
                                    professionalID={Professional.ID}
                                    professionalTitle={Professional.Title}
                                    primaryCTALabel={
                                        PrimaryCTA.primaryCTALabelTitleCase
                                    }
                                    branchOptions={branchOptions}
                                    companyLogo={Professional.CompanyLogo ?? ''}
                                    logoBackgroundColor={
                                        Professional.LogoBackgroundColor ??
                                        'white'
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export default PageHeader;
